/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Badge,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { apiget } from '../../../service/api';
import { ACTIVITY_TYPES, getActivityMeta } from './timelineConfig';
import ActivityTimelineItem from './ActivityTimelineItem';
import Addnotes from '../../../components/note/AddNotes';
import Addcalls from '../../../components/call/Addcalls';
import Addemail from '../../../components/email/Addemail';
import Addmeetings from '../../../components/meeting/Addmeetings';
import AddTask from '../../../components/task/AddTask';
import ComposeEmail from './ComposeEmail';
import SmsPanel from './SmsPanel';
import CallDispositionDialog from './CallDispositionDialog';

const ActivityTimeline = ({ leadId, leadData, setUserAction }) => {
  const [timeline, setTimeline] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Dialog states
  const [openNote, setOpenNote] = useState(false);
  const [openCall, setOpenCall] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);
  const [openMeeting, setOpenMeeting] = useState(false);
  const [openTask, setOpenTask] = useState(false);
  const [openComposeEmail, setOpenComposeEmail] = useState(false);
  const [openSms, setOpenSms] = useState(false);
  const [openCallDisposition, setOpenCallDisposition] = useState(false);

  const fetchTimeline = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiget(`communication/timeline/${leadId}`);
      if (result && result.status === 200) {
        setTimeline(result.data.timeline || []);
        setCounts(result.data.counts || {});
      }
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (leadId) {
      fetchTimeline();
    }
  }, [leadId, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    if (setUserAction) {
      setUserAction(Date.now());
    }
  };

  // Handle activity creation success from any dialog
  const handleActivityCreated = (result) => {
    handleRefresh();
  };

  // Filter toggle
  const toggleFilter = (typeId) => {
    setActiveFilters((prev) => {
      if (prev.includes(typeId)) {
        return prev.filter((f) => f !== typeId);
      }
      return [...prev, typeId];
    });
  };

  const clearFilters = () => setActiveFilters([]);

  // Apply filters
  const filteredTimeline =
    activeFilters.length === 0
      ? timeline
      : timeline.filter((item) => activeFilters.includes(item.activityType));

  // Quick-add buttons config
  const quickAddButtons = [
    { id: 'note', label: 'Note', icon: 'mdi:note-plus-outline', onClick: () => setOpenNote(true) },
    { id: 'call', label: 'Call', icon: 'mdi:phone-plus', onClick: () => setOpenCallDisposition(true) },
    { id: 'email', label: 'Email', icon: 'mdi:email-plus-outline', onClick: () => setOpenComposeEmail(true) },
    { id: 'meeting', label: 'Meeting', icon: 'mdi:calendar-plus', onClick: () => setOpenMeeting(true) },
    { id: 'task', label: 'Task', icon: 'mdi:checkbox-marked-circle-plus-outline', onClick: () => setOpenTask(true) },
    { id: 'sms', label: 'SMS', icon: 'mdi:message-plus-outline', onClick: () => setOpenSms(true) },
  ];

  if (loading && timeline.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">
            Activity Timeline
            <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
              ({counts.total || 0} activities)
            </Typography>
          </Typography>
          <Tooltip title="Refresh timeline">
            <Button
              size="small"
              onClick={handleRefresh}
              startIcon={<Icon icon="mdi:refresh" />}
              sx={{ textTransform: 'capitalize' }}
            >
              Refresh
            </Button>
          </Tooltip>
        </Box>

        {/* Quick-add bar */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mb: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            flexWrap: 'wrap',
          }}
        >
          {quickAddButtons.map((btn) => {
            const meta = getActivityMeta(btn.id);
            return (
              <Button
                key={btn.id}
                variant="outlined"
                size="small"
                startIcon={<Icon icon={btn.icon} />}
                onClick={btn.onClick}
                sx={{
                  textTransform: 'capitalize',
                  borderColor: 'grey.300',
                  color: meta.color,
                  '&:hover': {
                    borderColor: meta.color,
                    bgcolor: `${meta.color}08`,
                  },
                }}
              >
                {btn.label}
              </Button>
            );
          })}
        </Box>

        {/* Filter chips */}
        <Box display="flex" gap={0.5} mb={2} flexWrap="wrap" alignItems="center">
          <Chip
            label="All"
            size="small"
            onClick={clearFilters}
            variant={activeFilters.length === 0 ? 'filled' : 'outlined'}
            color={activeFilters.length === 0 ? 'primary' : 'default'}
            sx={{ fontSize: '0.75rem' }}
          />
          {ACTIVITY_TYPES.map((type) => {
            const isActive = activeFilters.includes(type.id);
            const countKey = type.id === 'status_change' ? 'statusChanges' : `${type.id}s`;
            const count = counts[countKey] || 0;
            return (
              <Badge
                key={type.id}
                badgeContent={count}
                color="default"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    height: 16,
                    minWidth: 16,
                    top: 2,
                    right: 2,
                  },
                }}
              >
                <Chip
                  label={type.label}
                  size="small"
                  onClick={() => toggleFilter(type.id)}
                  variant={isActive ? 'filled' : 'outlined'}
                  sx={{
                    fontSize: '0.75rem',
                    ...(isActive
                      ? {
                          bgcolor: `${type.color}18`,
                          color: type.color,
                          borderColor: type.color,
                        }
                      : {}),
                  }}
                />
              </Badge>
            );
          })}
        </Box>

        {/* Timeline list */}
        <Box
          sx={{
            maxHeight: 'calc(100vh - 450px)',
            minHeight: 200,
            overflowY: 'auto',
            pr: 1,
          }}
        >
          {filteredTimeline.length === 0 ? (
            <Box display="flex" alignItems="center" justifyContent="center" p={4}>
              <Typography variant="body2" color="text.disabled" fontStyle="italic">
                {activeFilters.length > 0
                  ? 'No activities match the selected filters.'
                  : 'No activities recorded yet. Use the buttons above to add your first activity.'}
              </Typography>
            </Box>
          ) : (
            filteredTimeline.map((item, index) => (
              <ActivityTimelineItem
                key={item._id || index}
                item={item}
                isLast={index === filteredTimeline.length - 1}
              />
            ))
          )}
        </Box>
      </CardContent>

      {/* Existing Add Dialogs — reuse as-is */}
      <Addnotes
        open={openNote}
        handleClose={() => setOpenNote(false)}
        _id={leadId}
        setUserAction={handleActivityCreated}
        method="lead"
      />
      <Addcalls
        open={openCall}
        handleClose={() => setOpenCall(false)}
        _id={leadId}
        setUserAction={handleActivityCreated}
      />
      <Addemail
        open={openEmail}
        handleClose={() => setOpenEmail(false)}
        _id={leadId}
        setUserAction={handleActivityCreated}
        receiver={leadData}
      />
      <Addmeetings
        open={openMeeting}
        handleClose={() => setOpenMeeting(false)}
        _id={leadId}
        data={leadData}
        setUserAction={handleActivityCreated}
      />
      <AddTask
        open={openTask}
        handleClose={() => setOpenTask(false)}
        _id={leadId}
        setUserAction={handleActivityCreated}
        leadDatas={leadData}
      />

      {/* New Communication Dialogs */}
      <ComposeEmail
        open={openComposeEmail}
        handleClose={() => setOpenComposeEmail(false)}
        leadId={leadId}
        leadData={leadData}
        onSuccess={handleActivityCreated}
      />
      <SmsPanel
        open={openSms}
        handleClose={() => setOpenSms(false)}
        leadId={leadId}
        leadData={leadData}
        onSuccess={handleActivityCreated}
      />
      <CallDispositionDialog
        open={openCallDisposition}
        handleClose={() => setOpenCallDisposition(false)}
        leadId={leadId}
        leadData={leadData}
        onSuccess={handleActivityCreated}
      />
    </Card>
  );
};

export default ActivityTimeline;
